import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { Smile } from 'lucide-react';
import { useWidgetContainer } from '@/contexts/WidgetContext';

interface EmojiPickerButtonProps {
  onEmojiSelect: (emoji: string) => void;
  disabled?: boolean;
}

export function EmojiPickerButton({ 
  onEmojiSelect, 
  disabled = false 
}: EmojiPickerButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const portalContainer = useWidgetContainer();

  // Calculate button position when opening
  const updateButtonPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const pickerHeight = 400;
      const pickerWidth = 300;
      
      let top = rect.top - pickerHeight - 8; // 8px gap above button
      let left = rect.left;
      
      // Adjust if picker would go off the top of screen
      if (top < 8) {
        top = rect.bottom + 8; // Show below button instead
      }
      
      // Adjust if picker would go off the right of screen
      if (left + pickerWidth > window.innerWidth - 8) {
        left = window.innerWidth - pickerWidth - 8;
      }
      
      // Adjust if picker would go off the left of screen
      if (left < 8) {
        left = 8;
      }
      
      setButtonPosition({ top, left });
    }
  };

  // Handle clicking outside to close the picker and ESC key
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    // Listen on shadow root if available (events inside shadow DOM don't
    // bubble to document with the correct target), else fall back to document.
    const root = buttonRef.current?.getRootNode() || document;

    if (isOpen) {
      root.addEventListener('mousedown', handleClickOutside as EventListener);
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      root.removeEventListener('mousedown', handleClickOutside as EventListener);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen]);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    onEmojiSelect(emojiData.emoji);
    // DON'T close the picker - keep it open for multiple emoji selection
  };

  const handleToggle = () => {
    if (!isOpen) {
      updateButtonPosition();
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`p-1.5 text-gray-400 hover:text-gray-600 transition-colors ${isOpen ? 'bg-gray-200' : ''} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        title="Add emoji"
      >
        <Smile size={18} />
      </button>

      {isOpen && createPortal(
        <div
          ref={pickerRef}
          className="fixed z-[2147483647] shadow-2xl border rounded-lg bg-white"
          style={{
            top: buttonPosition.top,
            left: buttonPosition.left,
            pointerEvents: 'auto'
          }}
        >
          <EmojiPicker
            onEmojiClick={handleEmojiClick}
            width={300}
            height={400}
            previewConfig={{ showPreview: false }}
          />
        </div>,
        portalContainer || document.body
      )}
    </>
  );
}

